/**
 * Modo do motion da página (um só por página): 'on' anima, 'off' deixa tudo parado no estado final (o HTML do servidor).
 * Sem React context de propósito: os blocos da Home hidratam em ilhas separadas (lazy-hydrate.tsx) e um contexto novo
 * não atravessaria. Módulo leve: só é consultado pelos blocos de um par com o motion ligado (ativacao.ts).
 *
 * Modo: Off com prefers-reduced-motion (sempre vence, inclusive a URL); senão `?motion=off|on` na URL (revisão e QA);
 * senão On. Sem distinguir robô de visitante: a SCRUM-1861 (#1049) tirou essa detecção do site inteiro — o que para uma
 * animação agora é o mesmo para todo mundo (movimento reduzido e ociosidade). Se o visitante ligar o movimento reduzido com a página aberta, o modo vira Off e
 * quem assina (useMotionBloco, useMotionOff) desmonta as cenas e para os laços na hora.
 */
export type MotionMode = 'off' | 'on';

const MODES: readonly MotionMode[] = ['off', 'on'];
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

let currentMode: MotionMode | null = null;
let consumidores = 0;
/** A lista de mídia em que o ouvinte foi posto (a mesma instância sai na saída do último consumidor). */
let midiaReduzida: MediaQueryList | null = null;
const listeners = new Set<() => void>();

const isMode = (value: unknown): value is MotionMode => typeof value === 'string' && (MODES as readonly string[]).includes(value);

const prefersReducedMotion = (): boolean => window.matchMedia?.(REDUCED_MOTION_QUERY)?.matches ?? false;

function fromUrl(): MotionMode | null {
  try {
    const value = new URLSearchParams(window.location.search).get('motion');
    return isMode(value) ? value : null;
  } catch {
    return null;
  }
}

function computeMode(): MotionMode {
  if (prefersReducedMotion()) return 'off';
  const url = fromUrl();
  if (url) return url;
  return 'on';
}

/** Movimento reduzido ligado ou desligado com a página aberta: recalcula, marca o <html> e avisa quem assina. */
function onReducedMotionChange(): void {
  if (consumidores === 0) return;
  const next = computeMode();
  if (next === currentMode) return;
  currentMode = next;
  document.documentElement.dataset.motion = next;
  listeners.forEach((listener) => listener());
}

/** Modo atual (sem efeito colateral: pode ser lido durante o render). No servidor, 'on' (o HTML é o estado final). */
export function getMotionMode(): MotionMode {
  if (typeof window === 'undefined') return 'on';
  if (currentMode === null) currentMode = computeMode();
  return currentMode;
}

export const isMotionMoving = (): boolean => getMotionMode() === 'on';

/**
 * Um bloco de um par da lista passa a usar o motion (efeito, nunca no render). O 1º consumidor recalcula o modo (URL e
 * movimento reduzido da página atual), marca `<html data-motion>` (liga os laços em CSS do kit) e passa a ouvir o
 * movimento reduzido; quando o último sai (navegação para uma página fora da lista), o atributo e o ouvinte saem e o
 * modo é esquecido: a página seguinte começa limpa. Devolve a saída.
 */
export function entrarNoMotion(): () => void {
  if (typeof document === 'undefined') return () => undefined;
  if (consumidores === 0) {
    currentMode = computeMode();
    document.documentElement.dataset.motion = currentMode;
    midiaReduzida = window.matchMedia?.(REDUCED_MOTION_QUERY) ?? null;
    midiaReduzida?.addEventListener?.('change', onReducedMotionChange);
  }
  consumidores += 1;
  let saiu = false;
  return () => {
    if (saiu) return;
    saiu = true;
    consumidores -= 1;
    if (consumidores > 0) return;
    midiaReduzida?.removeEventListener?.('change', onReducedMotionChange);
    midiaReduzida = null;
    delete document.documentElement.dataset.motion;
    currentMode = null;
  };
}

/** Assina as trocas de modo (só acontecem com a mudança do movimento reduzido). Devolve o cancelamento. */
export function onMotionModeChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Celular/toque: cenas de scrub viram cenas por tempo, distâncias menores. */
export const isCompactViewport = (): boolean => typeof window !== 'undefined' && (window.matchMedia?.('(max-width: 767px)')?.matches ?? false);

/** Só para testes: esquece o modo calculado. */
export function resetMotionModeForTests(): void {
  currentMode = null;
  consumidores = 0;
  listeners.clear();
}
